import React, { useState, useEffect } from 'react';
import { Search, RefreshCw, Download, Filter, Database, FileText, Globe, Calendar, AlertTriangle, Shield } from 'lucide-react';
import axios from 'axios';

const CriticalMineralPoliciesTable = () => {
  const [policies, setPolicies] = useState([]);
  const [loading, setLoading] = useState(false);
  const [filteredPolicies, setFilteredPolicies] = useState([]);
  const [searchText, setSearchText] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [countryFilter, setCountryFilter] = useState('all');
  const [mineralFilter, setMineralFilter] = useState('all');
  const [familyFilter, setFamilyFilter] = useState('all');
  const [topicFilter, setTopicFilter] = useState('all');
  const [tradeFilter, setTradeFilter] = useState('all');
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(20);
  const [sortField, setSortField] = useState('policy_id');
  const [sortDirection, setSortDirection] = useState('desc');

  // Fetch policies data
  const fetchPolicies = async () => {
    setLoading(true);
    try {
      const response = await axios.get('/api/critical-mineral-policies');
      setPolicies(response.data);
      setFilteredPolicies(response.data);
    } catch (error) {
      console.error('Error fetching policies:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchPolicies();
  }, []);

  // Filter policies based on search and filters
  useEffect(() => {
    let filtered = policies;

    // Search filter
    if (searchText) {
      filtered = filtered.filter(policy =>
        policy.policy_title?.toLowerCase().includes(searchText.toLowerCase()) ||
        policy.policy_description?.toLowerCase().includes(searchText.toLowerCase()) ||
        policy.country_name?.toLowerCase().includes(searchText.toLowerCase()) ||
        policy.mineral_name?.toLowerCase().includes(searchText.toLowerCase()) ||
        policy.policy_family?.toLowerCase().includes(searchText.toLowerCase()) ||
        policy.policy_topic?.toLowerCase().includes(searchText.toLowerCase()) ||
        policy.source_name?.toLowerCase().includes(searchText.toLowerCase())
      );
    }

    // Status filter
    if (statusFilter !== 'all') {
      filtered = filtered.filter(policy => policy.policy_status === statusFilter);
    }

    // Country filter
    if (countryFilter !== 'all') {
      filtered = filtered.filter(policy => policy.country_name === countryFilter);
    }

    // Mineral filter
    if (mineralFilter !== 'all') {
      filtered = filtered.filter(policy => policy.mineral_name === mineralFilter);
    }

    // Family filter
    if (familyFilter !== 'all') {
      filtered = filtered.filter(policy => policy.policy_family === familyFilter);
    }

    // Topic filter
    if (topicFilter !== 'all') {
      filtered = filtered.filter(policy => policy.policy_topic === topicFilter);
    }

    // Trade filter
    if (tradeFilter !== 'all') {
      if (tradeFilter === 'trade') {
        filtered = filtered.filter(policy => policy.is_trade_related === true);
      } else if (tradeFilter === 'non-trade') {
        filtered = filtered.filter(policy => policy.is_trade_related === false);
      }
    }

    setFilteredPolicies(filtered);
    setCurrentPage(1);
  }, [policies, searchText, statusFilter, countryFilter, mineralFilter, familyFilter, topicFilter, tradeFilter]);

  // Get unique values for filters
  const statuses = [...new Set(policies.map(policy => policy.policy_status).filter(Boolean))];
  const countries = [...new Set(policies.map(policy => policy.country_name).filter(Boolean))];
  const minerals = [...new Set(policies.map(policy => policy.mineral_name).filter(Boolean))];
  const families = [...new Set(policies.map(policy => policy.policy_family).filter(Boolean))];
  const topics = [...new Set(policies.map(policy => policy.policy_topic).filter(Boolean))];

  // Function to parse multiple classifications from JSON
  const parseMultipleClassifications = (policy) => {
    try {
      if (policy.policy_type) {
        const classifications = JSON.parse(policy.policy_type);
        if (Array.isArray(classifications)) {
          return {
            families: [...new Set(classifications.map(c => c.family).filter(Boolean))],
            topics: [...new Set(classifications.map(c => c.topic).filter(Boolean))]
          };
        }
      }
    } catch (e) {
      // Fallback to single classification
    }
    return {
      families: policy.policy_family ? [policy.policy_family] : [],
      topics: policy.policy_topic ? [policy.policy_topic] : []
    };
  };

  // Status color mapping
  const getStatusColor = (status) => {
    switch (status?.toLowerCase()) {
      case 'active': return 'bg-green-100 text-green-800 border-green-200';
      case 'inactive': return 'bg-gray-100 text-gray-800 border-gray-200';
      case 'draft': return 'bg-yellow-100 text-yellow-800 border-yellow-200';
      case 'repealed': return 'bg-red-100 text-red-800 border-red-200';
      case 'expired': return 'bg-orange-100 text-orange-800 border-orange-200';
      default: return 'bg-gray-100 text-gray-800 border-gray-200';
    }
  };

  // Policy family color mapping
  const getPolicyFamilyColor = (family) => {
    switch (family?.toLowerCase()) {
      case 'regulations': return 'bg-red-100 text-red-800 border-red-200';
      case 'framework legislation': return 'bg-blue-100 text-blue-800 border-blue-200';
      case 'incentives and investments': return 'bg-green-100 text-green-800 border-green-200';
      case 'trade policies': return 'bg-orange-100 text-orange-800 border-orange-200';
      case 'resource push': return 'bg-purple-100 text-purple-800 border-purple-200';
      default: return 'bg-gray-100 text-gray-800 border-gray-200';
    }
  };

  // Policy topic color mapping
  const getPolicyTopicColor = (topic) => {
    switch (topic?.toLowerCase()) {
      case 'critical minerals': return 'bg-indigo-100 text-indigo-800 border-indigo-200';
      case 'power': return 'bg-yellow-100 text-yellow-800 border-yellow-200';
      case 'transport': return 'bg-cyan-100 text-cyan-800 border-cyan-200';
      case 'people-centred clean energy transitions': return 'bg-pink-100 text-pink-800 border-pink-200';
      case 'methane abatement': return 'bg-teal-100 text-teal-800 border-teal-200';
      default: return 'bg-gray-100 text-gray-800 border-gray-200';
    }
  };

  // Sort function
  const handleSort = (field) => {
    if (sortField === field) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDirection('asc');
    }
  };

  // Get sorted and paginated data
  const getSortedData = () => {
    const sorted = [...filteredPolicies].sort((a, b) => {
      let aVal = a[sortField];
      let bVal = b[sortField];

      if (typeof aVal === 'string') {
        aVal = aVal.toLowerCase();
        bVal = bVal?.toLowerCase() || '';
      }

      if (sortDirection === 'asc') {
        return aVal > bVal ? 1 : -1;
      } else {
        return aVal < bVal ? 1 : -1;
      }
    });

    const startIndex = (currentPage - 1) * itemsPerPage;
    return sorted.slice(startIndex, startIndex + itemsPerPage);
  };

  // Export functionality
  const handleExport = () => {
    const csvContent = [
      // Headers
      ['Policy ID', 'Title', 'Description', 'Status', 'Year', 'Jurisdiction', 'Source', 'Date Enacted', 'Date Repealed', 'Country', 'Policy Family', 'Policy Topic', 'Mineral', 'Trade Related', 'Reference URL'].join(','),
      // Data rows
      ...filteredPolicies.map(policy => [
        policy.policy_id,
        `"${policy.policy_title?.replace(/"/g, '""') || ''}"`,
        `"${policy.policy_description?.replace(/"/g, '""') || ''}"`,
        policy.policy_status || '',
        policy.policy_year || '',
        policy.jurisdiction_level || '',
        `"${policy.source_name?.replace(/"/g, '""') || ''}"`,
        policy.date_enacted || '',
        policy.date_repealed || '',
        `"${policy.country_name?.replace(/"/g, '""') || ''}"`,
        policy.policy_family || '',
        policy.policy_topic || '',
        policy.mineral_name || '',
        policy.is_trade_related ? 'Yes' : 'No',
        `"${policy.reference_url?.replace(/"/g, '""') || ''}"`
      ].join(','))
    ].join('\n');

    const blob = new Blob([csvContent], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `critical_mineral_policies_${new Date().toISOString().split('T')[0]}.csv`;
    link.click();
    window.URL.revokeObjectURL(url);
  };

  const sortedData = getSortedData();
  const totalPages = Math.ceil(filteredPolicies.length / itemsPerPage);

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-64">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600 mx-auto mb-4"></div>
          <p className="text-gray-600">Loading policies...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-900">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Header */}
        <div className="mb-8">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-3xl font-bold text-gray-900 dark:text-white flex items-center">
                <FileText className="h-8 w-8 mr-3 text-primary-600" />
                Critical Mineral Policies Data Table
              </h1>
              <p className="mt-2 text-gray-600 dark:text-gray-400">
                Complete view of all critical mineral policies with filtering and export capabilities
              </p>
            </div>
            <div className="flex space-x-3">
              <button
                onClick={fetchPolicies}
                disabled={loading}
                className="inline-flex items-center px-4 py-2 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 disabled:opacity-50"
              >
                <RefreshCw className={`h-4 w-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
                Refresh
              </button>
              <button
                onClick={handleExport}
                className="inline-flex items-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-primary-600 hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500"
              >
                <Download className="h-4 w-4 mr-2" />
                Export CSV
              </button>
            </div>
          </div>
        </div>

        {/* Statistics Cards */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-8">
          <div className="bg-white dark:bg-gray-800 overflow-hidden shadow rounded-lg">
            <div className="p-5">
              <div className="flex items-center">
                <div className="flex-shrink-0">
                  <Database className="h-6 w-6 text-gray-400" />
                </div>
                <div className="ml-5 w-0 flex-1">
                  <dl>
                    <dt className="text-sm font-medium text-gray-500 dark:text-gray-400 truncate">
                      Total Policies
                    </dt>
                    <dd className="text-lg font-medium text-gray-900 dark:text-white">
                      {policies.length}
                    </dd>
                  </dl>
                </div>
              </div>
            </div>
          </div>

          <div className="bg-white dark:bg-gray-800 overflow-hidden shadow rounded-lg">
            <div className="p-5">
              <div className="flex items-center">
                <div className="flex-shrink-0">
                  <Filter className="h-6 w-6 text-gray-400" />
                </div>
                <div className="ml-5 w-0 flex-1">
                  <dl>
                    <dt className="text-sm font-medium text-gray-500 dark:text-gray-400 truncate">
                      Filtered Results
                    </dt>
                    <dd className="text-lg font-medium text-gray-900 dark:text-white">
                      {filteredPolicies.length}
                    </dd>
                  </dl>
                </div>
              </div>
            </div>
          </div>

          <div className="bg-white dark:bg-gray-800 overflow-hidden shadow rounded-lg">
            <div className="p-5">
              <div className="flex items-center">
                <div className="flex-shrink-0">
                  <Globe className="h-6 w-6 text-gray-400" />
                </div>
                <div className="ml-5 w-0 flex-1">
                  <dl>
                    <dt className="text-sm font-medium text-gray-500 dark:text-gray-400 truncate">
                      Active Policies
                    </dt>
                    <dd className="text-lg font-medium text-gray-900 dark:text-white">
                      {policies.filter(p => p.policy_status === 'Active').length}
                    </dd>
                  </dl>
                </div>
              </div>
            </div>
          </div>

          <div className="bg-white dark:bg-gray-800 overflow-hidden shadow rounded-lg">
            <div className="p-5">
              <div className="flex items-center">
                <div className="flex-shrink-0">
                  <AlertTriangle className="h-6 w-6 text-gray-400" />
                </div>
                <div className="ml-5 w-0 flex-1">
                  <dl>
                    <dt className="text-sm font-medium text-gray-500 dark:text-gray-400 truncate">
                      Trade-Related Policies
                    </dt>
                    <dd className="text-lg font-medium text-gray-900 dark:text-white">
                      {policies.filter(p => p.is_trade_related === true).length}
                    </dd>
                  </dl>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Filters */}
        <div className="bg-white dark:bg-gray-800 shadow rounded-lg mb-6">
          <div className="px-6 py-4 border-b border-gray-200 dark:border-gray-700">
            <h3 className="text-lg font-medium text-gray-900 dark:text-white">Filters</h3>
          </div>
          <div className="p-6">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Search
                </label>
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
                  <input
                    type="text"
                    placeholder="Search policies, countries, minerals..."
                    value={searchText}
                    onChange={(e) => setSearchText(e.target.value)}
                    className="w-full pl-10 pr-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500 dark:bg-gray-700 dark:border-gray-600 dark:text-white"
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Status
                </label>
                <select
                  value={statusFilter}
                  onChange={(e) => setStatusFilter(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500 dark:bg-gray-700 dark:border-gray-600 dark:text-white"
                >
                  <option value="all">All Statuses</option>
                  {statuses.map(status => (
                    <option key={status} value={status}>
                      {status?.toUpperCase() || 'N/A'}
                    </option>
                  ))}
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Country
                </label>
                <select
                  value={countryFilter}
                  onChange={(e) => setCountryFilter(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500 dark:bg-gray-700 dark:border-gray-600 dark:text-white"
                >
                  <option value="all">All Countries</option>
                  {countries.map(country => (
                    <option key={country} value={country}>
                      {country || 'N/A'}
                    </option>
                  ))}
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Mineral
                </label>
                <select
                  value={mineralFilter}
                  onChange={(e) => setMineralFilter(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500 dark:bg-gray-700 dark:border-gray-600 dark:text-white"
                >
                  <option value="all">All Minerals</option>
                  {minerals.map(mineral => (
                    <option key={mineral} value={mineral}>
                      {mineral || 'N/A'}
                    </option>
                  ))}
                </select>
              </div>
            </div>
            
            {/* Second row of filters */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Policy Family
                </label>
                <select
                  value={familyFilter}
                  onChange={(e) => setFamilyFilter(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500 dark:bg-gray-700 dark:border-gray-600 dark:text-white"
                >
                  <option value="all">All Families</option>
                  {families.map(family => (
                    <option key={family} value={family}>
                      {family || 'N/A'}
                    </option>
                  ))}
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Policy Topic
                </label>
                <select
                  value={topicFilter}
                  onChange={(e) => setTopicFilter(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500 dark:bg-gray-700 dark:border-gray-600 dark:text-white"
                >
                  <option value="all">All Topics</option>
                  {topics.map(topic => (
                    <option key={topic} value={topic}>
                      {topic || 'N/A'}
                    </option>
                  ))}
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Trade Related
                </label>
                <select
                  value={tradeFilter}
                  onChange={(e) => setTradeFilter(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500 dark:bg-gray-700 dark:border-gray-600 dark:text-white"
                >
                  <option value="all">All Policies</option>
                  <option value="trade">Trade Related</option>
                  <option value="non-trade">Non-Trade</option>
                </select>
              </div>
            </div>
          </div>
        </div>

        {/* Table */}
        <div className="bg-white dark:bg-gray-800 shadow rounded-lg overflow-hidden">
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
              <thead className="bg-gray-50 dark:bg-gray-700">
                <tr>
                  <th 
                    className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider cursor-pointer hover:bg-gray-100 dark:hover:bg-gray-600"
                    onClick={() => handleSort('policy_id')}
                  >
                    ID {sortField === 'policy_id' && (sortDirection === 'asc' ? '↑' : '↓')}
                  </th>
                  <th 
                    className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider cursor-pointer hover:bg-gray-100 dark:hover:bg-gray-600"
                    onClick={() => handleSort('policy_title')}
                  >
                    Title {sortField === 'policy_title' && (sortDirection === 'asc' ? '↑' : '↓')}
                  </th>
                  <th 
                    className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider cursor-pointer hover:bg-gray-100 dark:hover:bg-gray-600"
                    onClick={() => handleSort('policy_status')}
                  >
                    Status {sortField === 'policy_status' && (sortDirection === 'asc' ? '↑' : '↓')}
                  </th>
                  <th 
                    className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider cursor-pointer hover:bg-gray-100 dark:hover:bg-gray-600"
                    onClick={() => handleSort('country_name')}
                  >
                    Country {sortField === 'country_name' && (sortDirection === 'asc' ? '↑' : '↓')}
                  </th>
                  <th 
                    className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider cursor-pointer hover:bg-gray-100 dark:hover:bg-gray-600"
                    onClick={() => handleSort('mineral_name')}
                  >
                    Mineral {sortField === 'mineral_name' && (sortDirection === 'asc' ? '↑' : '↓')}
                  </th>
                  <th 
                    className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider cursor-pointer hover:bg-gray-100 dark:hover:bg-gray-600"
                    onClick={() => handleSort('policy_family')}
                  >
                    Family {sortField === 'policy_family' && (sortDirection === 'asc' ? '↑' : '↓')}
                  </th>
                  <th 
                    className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider cursor-pointer hover:bg-gray-100 dark:hover:bg-gray-600"
                    onClick={() => handleSort('policy_topic')}
                  >
                    Topic {sortField === 'policy_topic' && (sortDirection === 'asc' ? '↑' : '↓')}
                  </th>
                  <th 
                    className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider cursor-pointer hover:bg-gray-100 dark:hover:bg-gray-600"
                    onClick={() => handleSort('policy_year')}
                  >
                    Year {sortField === 'policy_year' && (sortDirection === 'asc' ? '↑' : '↓')}
                  </th>
                  <th 
                    className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider cursor-pointer hover:bg-gray-100 dark:hover:bg-gray-600"
                    onClick={() => handleSort('date_enacted')}
                  >
                    Date Enacted {sortField === 'date_enacted' && (sortDirection === 'asc' ? '↑' : '↓')}
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                    Source
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                    Reference
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-700">
                {sortedData.map((policy) => (
                  <tr key={policy.policy_id} className="hover:bg-gray-50 dark:hover:bg-gray-700">
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900 dark:text-white">
                      {policy.policy_id}
                    </td>
                    <td className="px-6 py-4 text-sm text-gray-900 dark:text-white max-w-xs">
                      <div className="truncate" title={policy.policy_title}>
                        {policy.policy_title}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full border ${getStatusColor(policy.policy_status)}`}>
                        {policy.policy_status?.toUpperCase() || 'N/A'}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 dark:text-gray-400">
                      {policy.country_name || 'N/A'}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 dark:text-gray-400">
                      {policy.mineral_name || 'N/A'}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="flex flex-col space-y-1">
                        <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full border ${getPolicyFamilyColor(policy.policy_family)}`}>
                          {policy.policy_family?.toUpperCase() || 'N/A'}
                        </span>
                        {policy.is_trade_related && (
                          <span className="inline-flex px-2 py-1 text-xs font-semibold rounded-full border bg-orange-100 text-orange-800 border-orange-200">
                            TRADE
                          </span>
                        )}
                        {(() => {
                          const classifications = parseMultipleClassifications(policy);
                          if (classifications.families.length > 1) {
                            return (
                              <div className="text-xs text-gray-500">
                                +{classifications.families.length - 1} more
                              </div>
                            );
                          }
                          return null;
                        })()}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="flex flex-col space-y-1">
                        <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full border ${getPolicyTopicColor(policy.policy_topic)}`}>
                          {policy.policy_topic?.toUpperCase() || 'N/A'}
                        </span>
                        {(() => {
                          const classifications = parseMultipleClassifications(policy);
                          if (classifications.topics.length > 1) {
                            return (
                              <div className="text-xs text-gray-500">
                                +{classifications.topics.length - 1} more
                              </div>
                            );
                          }
                          return null;
                        })()}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 dark:text-gray-400">
                      {policy.policy_year || 'N/A'}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 dark:text-gray-400">
                      {policy.date_enacted ? new Date(policy.date_enacted).toLocaleDateString() : 'N/A'}
                    </td>
                    <td className="px-6 py-4 text-sm text-gray-500 dark:text-gray-400 max-w-xs">
                      <div className="truncate" title={policy.source_name}>
                        {policy.source_name || 'N/A'}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 dark:text-gray-400">
                      {policy.reference_url ? (
                        <a 
                          href={policy.reference_url} 
                          target="_blank" 
                          rel="noopener noreferrer"
                          className="text-primary-600 hover:text-primary-800"
                        >
                          View
                        </a>
                      ) : (
                        'N/A'
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* Pagination */}
          <div className="bg-white dark:bg-gray-800 px-4 py-3 flex items-center justify-between border-t border-gray-200 dark:border-gray-700 sm:px-6">
            <div className="flex-1 flex justify-between sm:hidden">
              <button
                onClick={() => setCurrentPage(Math.max(1, currentPage - 1))}
                disabled={currentPage === 1}
                className="relative inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Previous
              </button>
              <button
                onClick={() => setCurrentPage(Math.min(totalPages, currentPage + 1))}
                disabled={currentPage === totalPages}
                className="ml-3 relative inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Next
              </button>
            </div>
            <div className="hidden sm:flex-1 sm:flex sm:items-center sm:justify-between">
              <div>
                <p className="text-sm text-gray-700 dark:text-gray-300">
                  Showing{' '}
                  <span className="font-medium">
                    {Math.min((currentPage - 1) * itemsPerPage + 1, filteredPolicies.length)}
                  </span>{' '}
                  to{' '}
                  <span className="font-medium">
                    {Math.min(currentPage * itemsPerPage, filteredPolicies.length)}
                  </span>{' '}
                  of{' '}
                  <span className="font-medium">{filteredPolicies.length}</span>{' '}
                  results
                </p>
              </div>
              <div className="flex items-center space-x-2">
                <select
                  value={itemsPerPage}
                  onChange={(e) => {
                    setItemsPerPage(Number(e.target.value));
                    setCurrentPage(1);
                  }}
                  className="px-3 py-1 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-primary-500 dark:bg-gray-700 dark:border-gray-600 dark:text-white"
                >
                  <option value={10}>10 per page</option>
                  <option value={20}>20 per page</option>
                  <option value={50}>50 per page</option>
                  <option value={100}>100 per page</option>
                </select>
                <div className="flex space-x-1">
                  <button
                    onClick={() => setCurrentPage(Math.max(1, currentPage - 1))}
                    disabled={currentPage === 1}
                    className="relative inline-flex items-center px-2 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-500 bg-white hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    Previous
                  </button>
                  <button
                    onClick={() => setCurrentPage(Math.min(totalPages, currentPage + 1))}
                    disabled={currentPage === totalPages}
                    className="relative inline-flex items-center px-2 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-500 bg-white hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    Next
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CriticalMineralPoliciesTable;
